import React from "react";
import { ArrowRight, Sparkles, ShieldCheck, Leaf, Hammer } from "lucide-react";
import { useApp } from "@/context/AppContext";

export default function Hero() {
  const { t } = useApp();

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      {/* Subtle grid background */}
      <div className="ht-grid-bg pointer-events-none absolute inset-0 opacity-40" />
      {/* Soft accent blob */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full opacity-25"
        style={{ background: "radial-gradient(closest-side, var(--ht-accent), transparent 70%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 bottom-0 h-[500px] w-[500px] rounded-full opacity-20"
        style={{ background: "radial-gradient(closest-side, var(--ht-primary), transparent 70%)" }}
      />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 pb-16 pt-16 sm:px-6 sm:pb-24 sm:pt-24 lg:grid-cols-12 lg:px-8">
        {/* Text column */}
        <div className="lg:col-span-7">
          <p className="ht-eyebrow ht-fade-up" data-testid="hero-eyebrow">
            {t.hero.eyebrow}
          </p>
          <h1
            className="ht-fade-up mt-4 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl"
            style={{ color: "var(--ht-text)" }}
            data-testid="hero-title"
          >
            {t.hero.titleA}{" "}
            <span style={{ color: "var(--ht-primary)" }}>{t.hero.titleB}</span>
            <br />
            <span className="inline-flex items-center gap-2">
              <Sparkles className="inline-block" size={28} style={{ color: "var(--ht-accent)" }} />
              <span style={{ color: "var(--ht-accent)" }}>{t.hero.titleC}</span>
            </span>
          </h1>
          <p
            className="ht-fade-up mt-6 max-w-2xl text-base sm:text-lg"
            style={{ color: "var(--ht-text-muted)", animationDelay: "120ms" }}
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </p>

          {/* Coming soon pill for Curso Manitas */}
          <div
            className="ht-fade-up mt-4 inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.16em]"
            style={{
              borderColor: "rgba(var(--ht-accent-rgb), 0.35)",
              backgroundColor: "rgba(var(--ht-accent-rgb), 0.1)",
              color: "var(--ht-accent)",
              animationDelay: "160ms",
            }}
            data-testid="hero-coming-soon-pill"
          >
            <span
              className="ht-pulse h-2 w-2 rounded-full"
              style={{ backgroundColor: "var(--ht-accent)" }}
            />
            {t.hero.comingSoonPill}
          </div>

          <div className="ht-fade-up mt-8 flex flex-wrap gap-3" style={{ animationDelay: "220ms" }}>
            <a
              href="#contacto"
              data-testid="hero-cta-primary"
              className="ht-btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={16} />
            </a>
            <a
              href="#academia"
              data-testid="hero-cta-secondary"
              className="ht-btn-accent inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
            >
              {t.hero.ctaSecondary}
              <Sparkles size={16} />
            </a>
          </div>

          {/* Stats */}
          <div
            className="ht-fade-up mt-10 grid max-w-2xl grid-cols-3 gap-4"
            style={{ animationDelay: "320ms" }}
          >
            {[
              { icon: <Hammer size={18} />, label: t.hero.stat1 },
              { icon: <ShieldCheck size={18} />, label: t.hero.stat2 },
              { icon: <Leaf size={18} />, label: t.hero.stat3 },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`hero-stat-${i}`}
                className="ht-card flex flex-col gap-1 px-3 py-3 text-xs sm:text-sm"
              >
                <span style={{ color: "var(--ht-primary)" }}>{s.icon}</span>
                <span className="font-medium" style={{ color: "var(--ht-text)" }}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Video */}
        <div className="relative lg:col-span-5">
          <div className="relative">
            <div
              className="ht-card ht-fade-up relative overflow-hidden rounded-xl"
              style={{ aspectRatio: "16/9" }}
              data-testid="hero-video-container"
            >
              <iframe
                src="https://www.youtube-nocookie.com/embed/KP3H3hPD3Tg?rel=0&modestbranding=1"
                title="Home a tope · Vídeo de presentación"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="absolute inset-0 h-full w-full border-0"
                data-testid="hero-video-iframe"
              />
            </div>
            <div
              className="ht-glass ht-fade-up absolute -right-3 -bottom-4 flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold sm:right-4"
              style={{ color: "var(--ht-primary)", animationDelay: "380ms" }}
              data-testid="hero-badge"
            >
              <span className="ht-pulse h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "var(--ht-accent)" }} />
              Asistente IA disponible
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
